import {
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  Container,
  Divider,
  Grid,
  Skeleton,
  Typography,
  Alert
} from "@mui/material";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { useEffect, useState } from "react";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import duration from "dayjs/plugin/duration";
import { useGetPurchaseOrdersPublicQuery } from "../../store/api";
import { useAppSelector } from "../../store/hooks/useAppSelector";
import { RootState } from "../../store/types/rootState";
import { PURCHASE_ORDER_STATUS } from "../../constants/purchaseOrderStatus";

dayjs.extend(relativeTime);
dayjs.extend(duration);

interface SelfVerificationProps {
  raffleId: string;
  email: string;
  activeRaffleName: string;
  limit: number;
}

export const SelfVerification = ({
  raffleId,
  activeRaffleName,
  email,
  limit,
}: SelfVerificationProps) => {
  const accountInformation = useAppSelector(({ account }: RootState) => account);
  const whatsappNumber = accountInformation.socialMediaList.find(sm => sm.name === 'supportWhatsapp')?.username;
  const { data: purchaseOrdersFounded, isLoading } = useGetPurchaseOrdersPublicQuery({ email, raffleId });
  const [now, setNow] = useState(dayjs());

  useEffect(() => {
    const interval = setInterval(() => {
      setNow(dayjs());
    }, 1000); // ahora se actualiza cada segundo
    return () => clearInterval(interval);
  }, []);

  return (
    <Container maxWidth="lg" sx={{ paddingBottom: 10 }}>
      <Grid container justifyContent="center" mt={5} spacing={2}>
        <Grid item xs={12} textAlign="center">
          <img
            src={accountInformation.logo}
            width={100}
            height={100}
            style={{ borderRadius: 20 }}
            alt="Logo"
          />
        </Grid>
        <Grid item xs={12} textAlign="center">
          <Typography variant="h5" fontWeight="bold">
            Estado de órdenes de compra: {activeRaffleName}
          </Typography>
        </Grid>
      </Grid>

      <Grid item xs={12}>
      <Alert
        severity="warning"
        icon={<WarningAmberIcon fontSize="inherit" />}
        sx={{ mt: 3 }}
      >
        Recuerde que los números serán validados dentro de las próximas 24 horas después del pago. 
        En caso de que la compra no sea verificada en ese plazo, soporte se pondrá en contacto con usted vía WhatsApp.
      </Alert>

      </Grid>

      <Grid container spacing={3} mt={2}>
        {isLoading ? (
          Array.from({ length: 3 }).map((_, index) => (
            <Grid item xs={12} md={6} lg={4} key={index}>
              <Skeleton variant="rectangular" height={280} />
            </Grid>
          ))
        ) : purchaseOrdersFounded?.length ? (
          purchaseOrdersFounded
          .map((order: any) => {
            const created = dayjs(order.createdDate);
            const deadline = created.add(24, "hour");
            const isExpired = now.isAfter(deadline);
            const remaining = dayjs.duration(deadline.diff(now));

            return (
              <Grid item xs={12} md={6} lg={4} key={order.id}>
                <Card elevation={3}>
                  <CardHeader
                    avatar={<Avatar>{order.purchaser.name.charAt(0)}</Avatar>}
                    title={<Typography fontWeight="bold">{order.purchaser.name}</Typography>}
                    subheader={<Typography variant="body2">Id: {order.purchaser.email}</Typography>}
                  />
                  <Divider />
                  <CardContent>
                    <Typography><strong>Tickets:</strong> {order.ticketQuantity}</Typography>
                    <Typography><strong>Total:</strong> ${order.totalAmount} ({order.totalAmountUSD} USD)</Typography>
                    <Typography><strong>Fecha de compra:</strong> {created.format("DD/MM/YYYY HH:mm")}</Typography>
                    <Typography sx={{ mt: 1 }}>
                      <strong>Estado:</strong>{" "}
                      <Chip
                        label={PURCHASE_ORDER_STATUS.find(pos => pos.id === order.status)?.id === 'SENDING_MAIL_ERROR' ? 'Proceso finalizado' : PURCHASE_ORDER_STATUS.find(pos => pos.id === order.status)?.title}
                        color={getStatusColor(order.status)}
                        variant="outlined"
                      />
                    </Typography>

                    <Divider sx={{ my: 2 }} />

                    {order.selectedTickets && (
                      <>
                        <Typography variant="body2" fontWeight="medium">Números{order.status === 'PURCHASE_ORDER_CANCELED' && ' cancelados'}:  </Typography>
                        <Box display="flex" flexWrap="wrap" gap={1} mt={1}>
                          {order.selectedTickets.map((ticket: number) => (
                            <Chip key={ticket} label={ticket.toString().padStart(limit.toString().length - 1, '0')} color={order.status === 'PURCHASE_ORDER_CANCELED' ? "error" : "primary"} size="small" />
                          ))}
                        </Box>
                      </>
                    )}

                    {order.status === 'TRANSFER_PAYMENT_CREATED' && <Typography mt={2}>
                      <Typography display={"block"}>Tiempo restante para validación:</Typography>{" "}
                      {isExpired ? (
                        <Chip label="Tiempo expirado" color="error" size="small" />
                      ) : (
                        <Chip
                          label={`${remaining.hours()}h ${remaining.minutes()}m ${remaining.seconds()}s`}
                          color="warning"
                          size="small"
                        />
                      )}
                    </Typography>}

                    {/* {order.status === 'SENDING_MAIL_ERROR' && (
                      <Box mt={2}>
                        <Button
                          variant="contained"
                          color="success"
                          href={`https://wa.me/${whatsappNumber}/?text=${encodeURIComponent("Hola, hubo un error al enviar mis números al correo, mi correo es: " + order.purchaser.email + ". Mi nombre es: " + order.purchaser.name + ". Necesito ayuda.")}`}
                          target="_blank"
                          fullWidth
                          startIcon={<WhatsAppIcon />}
                        >
                          Contactar por WhatsApp
                        </Button>
                      </Box>
                    )} */}
                    


                    {isExpired && order.status === 'TRANSFER_PAYMENT_CREATED' && (
                      <Box mt={2}>
                        <Button
                          variant="contained"
                          color="success"
                          href={`https://wa.me/${whatsappNumber}/?text=${encodeURIComponent("Hola, expiró el tiempo de espera para aprobar mis números, mi correo es: " + order.purchaser.email + ". Mi nombre es: " + order.purchaser.name + ". Necesito ayuda.")}`}
                          target="_blank"
                          fullWidth
                          startIcon={<WhatsAppIcon />}
                        >
                          Contactar por WhatsApp
                        </Button>
                      </Box>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            );
          })
        ) : (
          <Grid item xs={12} textAlign="center">
            <Typography variant="body1" color="textSecondary">
              No se encontraron órdenes de compra para este correo y rifa.
            </Typography>
          </Grid>
        )}
      </Grid>
    </Container>
  );
};

const getStatusColor = (status: string): "success" | "error" | "info" => {
  switch (status) {
    case "PROCESS_FINISHED":
    case "SENDING_MAIL_ERROR":
      return "success";
    case "PAYMENT_FAILED":
    case "PURCHASE_ORDER_REJECTED":
    case "PURCHASE_ORDER_CANCELED":
    case "CASH_PAYMENT_BAD_CREDENTIALS":
      return "error";
    default:
      return "info";
  }
};

export default SelfVerification;
